<template>
  <v-menu
    v-model="showMenu"
    location="bottom"
    :close-on-content-click="false"
  >
    <template #activator="{ props }">
      <v-text-field
        class="datetime-picker-field"
        @click="showMenu = true"
        v-bind="{ ...$attrs, ...props }"
        :model-value="boundValue || ''"
        :label="label"
        :clearable="clearable"
        prepend-inner-icon="mdi-calendar"
        readonly
        @click:clear.stop="clearDateTime"
      ></v-text-field>
    </template>

    <v-card
      class="datetime-picker"
    >
      <!-- Master used landscape pickers side-by-side -->
      <div class="datetime-picker__body">
        <v-date-picker
          :model-value="dateForPicker"
          :min="minDate || undefined"
          @update:model-value="onDateUpdate"
          :first-day-of-week="1"
          landscape
          width="auto"
        ></v-date-picker>
        <v-time-picker
          :model-value="time"
          :min="minTime || undefined"
          @update:model-value="onTimeUpdate"
          format="24hr"
          width="auto"
        ></v-time-picker>
      </div>

      <div
        class="d-flex pa-2"
      >
        <base-sidebar-btn
          v-if="clearable"
          @click="clearDateTime"
          label="Clear"
          icon="mdi-close"
          :btn-class="!$store.state.isDarkMode ? 'grey-lighten-5' : 'grey-darken-4'"
          :icon-color="!$store.state.isDarkMode ? 'grey' : 'white'"
          style="width: 120px;"
          class="mr-2"
        ></base-sidebar-btn>
        <base-sidebar-btn
          @click="confirmDateTime"
          label="Confirm"
          icon="mdi-check"
          :btn-class="!$store.state.isDarkMode ? 'blue-darken-2' : 'pink-lighten-2'"
          :icon-color="!$store.state.isDarkMode ? 'white' : 'white'"
          style="width: 170px;"
        ></base-sidebar-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import modelValueCompat from '@/mixins/modelValueCompat';

export default {
  name: 'BaseDatetimePicker',

  mixins: [modelValueCompat],

  inheritAttrs: false,

  props: {
    onlyFuture: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Date and Time',
    },
  },

  data: () => ({
    lastValue: null,
    // Always keep as strings matching master / API: YYYY-MM-DD and HH:MM[:SS]
    date: '',
    time: '',
    showMenu: false,
  }),

  computed: {
    minDate() {
      return this.onlyFuture ? this.formatDate(new Date()) : '';
    },
    minTime() {
      return (this.onlyFuture && this.date === this.minDate)
        ? (new Date()).toTimeString().slice(0, 8)
        : '';
    },
    /**
     * Vuetify 3 date picker prefers a Date (or adapter date). Feed it a Date
     * built from our YYYY-MM-DD string so selection round-trips cleanly.
     */
    dateForPicker() {
      if (!this.date || !/^\d{4}-\d{2}-\d{2}$/.test(this.date)) {
        return undefined;
      }
      const [y, m, d] = this.date.split('-').map(Number);
      return new Date(y, m - 1, d);
    },
  },

  watch: {
    boundValue() {
      this.loadDateTime();
    },
  },

  mounted() {
    this.loadDateTime();
  },

  methods: {
    /**
     * Master emitted `YYYY-MM-DD HH:MM[:SS]`. Vuetify 3 v-date-picker v-models a
     * Date object; concatenating it produced an unparseable string and the API
     * returned "The due date is not a valid date."
     */
    normalizeDate(val) {
      if (val == null || val === '') {
        return '';
      }
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        return this.formatDate(val);
      }
      if (typeof val === 'string') {
        const isoDay = val.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoDay) {
          return isoDay[1];
        }
        const parsed = new Date(val);
        if (!Number.isNaN(parsed.getTime())) {
          return this.formatDate(parsed);
        }
      }
      // Date adapter / other objects that coerce via Date()
      const parsed = new Date(val);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDate(parsed);
      }
      return '';
    },

    normalizeTime(val) {
      if (val == null || val === '') {
        return '00:00';
      }
      if (typeof val === 'string') {
        const m = val.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (m) {
          const hh = String(m[1]).padStart(2, '0');
          const mm = m[2];
          return m[3] != null ? `${hh}:${mm}:${m[3]}` : `${hh}:${mm}`;
        }
      }
      return '00:00';
    },

    loadDateTime() {
      if (this.lastValue && this.lastValue === this.boundValue) {
        return;
      }

      if (this.boundValue) {
        const parsed = new Date(Date.parse(this.boundValue));
        if (!Number.isNaN(parsed.getTime())) {
          // Use local calendar day (not toISOString) to avoid TZ day-shift
          this.date = this.formatDate(parsed);
          this.time = this.normalizeTime(parsed.toTimeString().slice(0, 8));
        } else {
          this.date = this.formatDate(new Date());
          this.time = '00:00';
        }
        this.changeDateTime();
        return;
      }

      // Unset: seed picker defaults for the next open, but do not emit a value.
      this.date = this.formatDate(new Date());
      this.time = '00:00';
      this.lastValue = null;
    },

    onDateUpdate(val) {
      this.date = this.normalizeDate(val);
      this.changeDateTime();
    },

    onTimeUpdate(val) {
      this.time = this.normalizeTime(val);
      this.changeDateTime();
    },

    changeDateTime() {
      const dateStr = this.normalizeDate(this.date);
      const timeStr = this.normalizeTime(this.time);
      if (!dateStr) {
        return;
      }
      const val = `${dateStr} ${timeStr}`;
      if (this.boundValue !== val) {
        this.lastValue = val;
        this.emitBoundValue(val);
      }
    },

    clearDateTime() {
      this.showMenu = false;
      this.date = this.formatDate(new Date());
      this.time = '00:00';
      this.lastValue = null;
      this.emitBoundValue(null);
    },

    formatDate(date) {
      return date.getFullYear()
        + '-' + (`0${date.getMonth() + 1}`).slice(-2)
        + '-' + (`0${date.getDate()}`).slice(-2);
    },

    confirmDateTime() {
      this.showMenu = false;
      this.changeDateTime();
    },
  },
};
</script>

<style>
.datetime-picker-field {
  min-width: 260px;
  width: 100%;
}

.datetime-picker .v-picker__title {
  border-radius: 0 !important;
}

.datetime-picker__body {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
}

.datetime-picker__body > * {
  flex: 0 0 auto;
}
</style>
